/**
 * A single icon-only nav row rendered in the collapsed sidebar icon-rail
 * (#2579, EPIC #2575 Tier 4).
 *
 * When the sidebar is collapsed to the `w-16` rail, group headers are hidden and
 * every nav destination is shown as an icon-only link. The label is surfaced via
 * a pure-CSS tooltip (a `group-hover`/`group-focus-within` positioned span — no
 * new dependency, no portal) AND the native `title` attribute for a11y/screen
 * readers. The active-route accent treatment mirrors the expanded NavLink so the
 * "where am I" cue survives collapse.
 *
 * The tooltip is positioned to the RIGHT of the rail (`left-full`) and never
 * clips because the sidebar is a top-level fixed element with nothing overlaying
 * its right edge; it is pointer-events-none so it can't trap the cursor.
 */
import { NavLink } from "react-router-dom";

interface RailNavItemProps {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  active: boolean;
  /** Where the rail icon navigates (grouped items point at their landing route). */
  onNavigate: () => void;
  /** data-tour hook, mirrors the expanded NavLink. */
  dataTour?: string;
}

export function RailNavItem({
  to,
  label,
  icon: Icon,
  active,
  onNavigate,
  dataTour,
}: RailNavItemProps) {
  return (
    <li className="group/rail relative">
      <NavLink
        to={to}
        data-tour={dataTour}
        onClick={onNavigate}
        title={label}
        aria-label={label}
        className={`
          flex items-center justify-center min-h-[44px] w-full rounded-lg
          transition-colors
          ${
            active
              ? "bg-brand-accent text-[#ffffff]"
              : "text-gray-300 hover:bg-navy-light hover:text-[#ffffff]"
          }
        `}
      >
        <Icon className="h-5 w-5 shrink-0" />
      </NavLink>
      {/* CSS-only tooltip — appears on hover/focus of the rail row. */}
      <span
        role="tooltip"
        className="
          pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50
          whitespace-nowrap rounded-md bg-navy-light px-2.5 py-1.5
          text-xs font-medium text-[#ffffff] shadow-lg
          opacity-0 group-hover/rail:opacity-100 group-focus-within/rail:opacity-100
          transition-opacity duration-150
          motion-reduce:transition-none
        "
      >
        {label}
      </span>
    </li>
  );
}
